/**
 * O PROGRESSO do painel da conversa (§5.1) — só com PLANO EXPLÍCITO do turno.
 *
 * Regra do produto: turno comum NÃO tem progresso em etapas (a linha
 * "Pensando... / Consultando..." da bolha continua sendo o indicador). A fonte
 * é o contrato `plano` do Modo Agente (spec-008, PR #2242): evento SSE
 * `{itens: [{id, texto, estado: 'pendente'|'em_andamento'|'concluido', erro?}], versao}`,
 * guardado no getter `plano` (`store/mutations.js::SET_PLANO`, régua de
 * versão) e persistido em `message.plano` no `end`. Aqui só se traduz para o
 * vocabulário de apresentação do painel (`titulo`/`status`); nada é derivado
 * de `thinkingSteps`/`toolParts` — sem plano, lista vazia.
 *
 * Funções puras. Sem `?.`/`??` (o Babel deste projeto não transpila).
 */

export const STATUS_DO_PASSO = Object.freeze({
  PENDENTE: 'pendente',
  EM_ANDAMENTO: 'em_andamento',
  CONCLUIDA: 'concluida',
  FALHOU: 'falhou',
})

// O vocabulário do contrato `plano` (spec-008) — `estado` de cada item.
export const ESTADO_DO_ITEM_DO_PLANO = Object.freeze({
  PENDENTE: 'pendente',
  EM_ANDAMENTO: 'em_andamento',
  CONCLUIDO: 'concluido',
})

const STATUS_POR_ESTADO = Object.freeze({
  [ESTADO_DO_ITEM_DO_PLANO.PENDENTE]: STATUS_DO_PASSO.PENDENTE,
  [ESTADO_DO_ITEM_DO_PLANO.EM_ANDAMENTO]: STATUS_DO_PASSO.EM_ANDAMENTO,
  [ESTADO_DO_ITEM_DO_PLANO.CONCLUIDO]: STATUS_DO_PASSO.CONCLUIDA,
})
const STATUS_ENCERRADOS = new Set([STATUS_DO_PASSO.CONCLUIDA, STATUS_DO_PASSO.FALHOU])
const PREFIXO_DO_ID = 'etapa-'

/** A última mensagem do assistente, ou `null`. */
export function ultimaRespostaDoAssistente(mensagens) {
  const lista = mensagens || []
  for (let i = lista.length - 1; i >= 0; i -= 1) {
    if (lista[i] && lista[i].role === 'assistant') return lista[i]
  }
  return null
}

// `erro` presente vence o `estado`: o item falhou. Estado fora do contrato
// vira `pendente` (não se afirma um andamento que o backend não disse).
function statusDoItem(item) {
  if (item.erro) return STATUS_DO_PASSO.FALHOU
  const conhecido = Object.hasOwn(STATUS_POR_ESTADO, item.estado)
  return conhecido ? STATUS_POR_ESTADO[item.estado] : STATUS_DO_PASSO.PENDENTE
}

// O texto auxiliar da etapa — o que o consultor lê embaixo do título do passo.
// `detalhe` quando o item traz um (o contrato atual não traz — ver abaixo);
// senão o MOTIVO da falha ([T024], `item.erro` — worker
// `planejamento/coordenador.py:308`/`:324`, backend `core/plano_vivo.py:171`):
// cor sozinha não diz O QUE falhou, e sem esta linha o motivo que o backend
// mandou morria no caminho entre o fio e a tela. Nada é inventado: sem os dois,
// fica vazio.
//
// ⚠️ `detalhe` em BRANCO NÃO cala o `erro` — e as DUAS frentes chegaram a esta
// mesma régua por caminhos diferentes, o que é a melhor evidência que ela tem:
//
//   • medição do contrato (esta branch): `detalhe` NÃO TEM PRODUTOR — não é
//     escrito por `core/plano_vivo.py` nem por `planejamento/coordenador.py`, e
//     não está nas `properties` de `$defs/PlanoItem`
//     (`contratos/events_agente.schema.json`, que declara só `id`/`texto`/
//     `estado`). Não existe, portanto, quem possa dizer "não mostre nada" com
//     uma string vazia. Já `erro` tem produtor e só é gravado quando é verdade
//     (`if erro:` nos dois lados), então é sempre um motivo REAL.
//   • review humana (#2364, release `ia-claude-code`): `detalhe: ''` com `erro`
//     mostraria uma etapa vermelha SEM MOTIVO — uma afirmação sobre a qual o
//     consultor não pode agir.
//
// Precedência: detalhe COM conteúdo > `erro` > vazio. O retorno é trimado nos
// dois ramos, por simetria com o `erro` logo abaixo.
//
// ⚠️ Esta função já foi declarada DUAS vezes neste arquivo (`93ace4168`, squash
// do #2360): o ESLint acusava `Identifier has already been declared` e, em
// runtime, vencia a última por hoisting. `npm run ci:jest` passava 13/13 mesmo
// assim — o babel-jest transpila para CommonJS e não aplica o erro; só o parser
// ESM do webpack o via. Há um drift-guard lendo o FONTE em
// `progresso-do-turno.unit.js` justamente para isso não voltar em silêncio.
function detalheDoItem(item) {
  const detalhe = typeof item.detalhe === 'string' ? item.detalhe.trim() : ''
  if (detalhe) return detalhe
  return typeof item.erro === 'string' ? item.erro.trim() : ''
}

/**
 * Os itens do `plano` como etapas do painel: sem `texto` o item sai; `id`
 * ausente vira `etapa-<n>`; `detalhe` é o do item ou, na etapa que FALHOU, o
 * motivo (`erro`) — ver `detalheDoItem`.
 *
 * @param {Object} plano `{itens, versao}` do getter `plano` ou de `message.plano`.
 * @returns {Array<{id: string, titulo: string, detalhe: string, status: string}>}
 * @example etapasDoPlano({ itens: [{ texto: 'Ler fila', estado: 'concluido' }], versao: 1 })
 *   // → [{ id: 'etapa-0', titulo: 'Ler fila', detalhe: '', status: 'concluida' }]
 * @example etapasDoPlano({ itens: [{ texto: 'Gravar', erro: 'sem recibo' }] })
 *   // → [{ id: 'etapa-0', titulo: 'Gravar', detalhe: 'sem recibo', status: 'falhou' }]
 */
export function etapasDoPlano(plano) {
  const itens = plano && Array.isArray(plano.itens) ? plano.itens : []
  return itens
    .filter(function (item) {
      return Boolean(item && typeof item.texto === 'string' && item.texto.trim())
    })
    .map(function (item, indice) {
      return {
        id: item.id ? String(item.id) : PREFIXO_DO_ID + indice,
        titulo: item.texto.trim(),
        detalhe: detalheDoItem(item),
        status: statusDoItem(item),
      }
    })
}

/**
 * Em voo: o plano AO VIVO (getter `plano`). Fora: o da última resposta do
 * assistente (`message.plano` — o `end` grava a última versão na mensagem).
 *
 * ★ **[18/09/2026] A JANELA CEGA do início do turno, e por que ela existe.**
 *
 * `resetChatState › _resetarCanaisDoAgente` despacha `resetPlano` assim que o
 * turno começa — correto, senão o plano do turno anterior sobreviveria como se
 * fosse do novo. Só que o plano NOVO só chega no primeiro evento `plano`, e no
 * intervalo (o round-trip) `planoAoVivo` é `null`: a seção "Progresso" SUMIA da
 * tela e voltava quando o evento chegava. O owner mediu isso clicando
 * "Continuar" nas etapas do onboarding — *"a parte de progresso SOME e depois
 * volta"*.
 *
 * Nessa janela a resposta honesta não é "não há plano", é "ainda não sei": o
 * último plano conhecido continua sendo a melhor descrição do que está
 * acontecendo, e é o que fica. Assim que o plano novo chega, ele vence — e o
 * caminho fora de voo não mudou.
 *
 * ★ **E o servidor passou a desfazer a ambiguidade.** `resource_chat.py`
 * anuncia um `plano` VAZIO (`{itens: [], versao: 0}`) logo depois do `start`,
 * em TODO turno. Com ele a leitura fica exata:
 *
 * | `planoAoVivo` | significa | a seção mostra |
 * |---|---|---|
 * | `null`/ausente | o `start` ainda não chegou | o último plano conhecido |
 * | `{itens: []}` | o turno DISSE que não tem etapas | nada |
 * | `{itens: [...]}` | o plano deste turno | ele |
 *
 * Por isso o recuo para o histórico só vale quando o campo é AUSENTE — um
 * `itens: []` explícito é resposta, não silêncio. Sem essa distinção, o turno
 * comum depois de um turno com plano seguiria mostrando o plano velho até
 * fechar.
 *
 * @param {Object}  entrada
 * @param {boolean} entrada.emVoo        `chatLoading`.
 * @param {Object}  entrada.planoAoVivo  getter `plano` do store (`{itens, versao}` ou `null`).
 * @param {Array}   entrada.mensagens    `messages` do store.
 * @example progressoDoTurno({ emVoo: false, mensagens: [] }) // → []
 */
export function progressoDoTurno(entrada) {
  const config = entrada || {}
  const doHistorico = () => {
    const resposta = ultimaRespostaDoAssistente(config.mensagens)
    return etapasDoPlano(resposta && resposta.plano)
  }
  if (!config.emVoo) return doHistorico()
  const aoVivo = etapasDoPlano(config.planoAoVivo)
  if (aoVivo.length) return aoVivo
  // O turno já se pronunciou (o `plano` vazio do `start`): "sem etapas" é o
  // fato, e o plano velho não volta à tela.
  if (planoFoiAnunciado(config.planoAoVivo)) return []
  return doHistorico()
}

/**
 * O turno já disse alguma coisa sobre o plano dele?
 *
 * `null`/ausente = ainda não; um objeto com `itens` array (mesmo vazio) = sim.
 */
function planoFoiAnunciado(plano) {
  return Boolean(plano && Array.isArray(plano.itens))
}

// A etapa que dá nome ao chip: a em andamento; senão a última encerrada.
function etapaDeReferencia(etapas) {
  const emAndamento = etapas.find((etapa) => etapa.status === STATUS_DO_PASSO.EM_ANDAMENTO)
  if (emAndamento) return emAndamento
  for (let i = etapas.length - 1; i >= 0; i -= 1) {
    if (STATUS_ENCERRADOS.has(etapas[i].status)) return etapas[i]
  }
  return etapas[0] || null
}

/**
 * O resumo do chip e do anel. A fração é a do PLANO (etapas concluídas /
 * total) — dado real; sem plano, `temPlano: false` e nada de anel.
 *
 * @param {Array} etapas saída de `progressoDoTurno`.
 * @returns {{temPlano: boolean, emAndamento: boolean, encerrado: boolean, titulo: string, concluidas: (number|null), total: (number|null)}}
 * @example resumoDoProgresso([]) // → { temPlano: false, emAndamento: false, encerrado: false, titulo: '', concluidas: null, total: null }
 */
export function resumoDoProgresso(etapas) {
  const lista = etapas || []
  if (!lista.length) {
    return { temPlano: false, emAndamento: false, encerrado: false, titulo: '', concluidas: null, total: null }
  }
  const referencia = etapaDeReferencia(lista)
  const concluidas = lista.filter(function (etapa) {
    return etapa.status === STATUS_DO_PASSO.CONCLUIDA
  }).length
  return {
    temPlano: true,
    emAndamento: lista.some(function (etapa) {
      return etapa.status === STATUS_DO_PASSO.EM_ANDAMENTO
    }),
    encerrado: lista.every(function (etapa) {
      return STATUS_ENCERRADOS.has(etapa.status)
    }),
    titulo: referencia ? referencia.titulo : '',
    concluidas: concluidas,
    total: lista.length,
  }
}
